import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";
import { getAuthToken, clearAuthToken } from "./utils/authToken";

// Setup axios interceptor to always include session token
// Uses secure in-memory + sessionStorage token management
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || ({} as any);
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401 with "Invalid token" or "Session not found", clear the session
    // But NOT for login/auth endpoints themselves
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/') || url.includes('demo-login');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('Invalid') || detail.includes('expired') || detail.includes('not found')) {
        clearAuthToken();
        // Redirect to login page if not already there
        if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Enable credentials for all requests
axios.defaults.withCredentials = true;

// ─── Suppress benign ResizeObserver loop warnings ─────────────────────────
// The "ResizeObserver loop completed with undelivered notifications" error is
// a browser-level warning triggered by Radix UI, Recharts, and resizable
// panels. It never breaks functionality, but the React dev-server error
// overlay surfaces it. We handle it two ways:
//   1. Wrap ResizeObserver so its callback is deferred to an animation frame
//      (eliminates the error at the source in modern browsers).
//   2. Capture the error in the earliest listener phase so the React overlay
//      never sees it.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class DebouncedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          try {
            callback(entries, observer);
          } catch (err) {
            if (err && typeof err.message === "string" && err.message.includes("ResizeObserver")) {
              return;
            }
            throw err;
          }
        });
      });
    }
  };
}

const RESIZE_OBSERVER_ERROR_SUBSTR = "ResizeObserver loop";
const isResizeObserverError = (msg) =>
  typeof msg === "string" && msg.includes(RESIZE_OBSERVER_ERROR_SUBSTR);

// Capture-phase listener registered as early as possible so it runs BEFORE
// the React dev-server error overlay's own listener.
window.addEventListener(
  "error",
  (e) => {
    if (isResizeObserverError(e.message) || isResizeObserverError(e.error?.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true,
);
window.addEventListener(
  "unhandledrejection",
  (e) => {
    if (isResizeObserverError(e?.reason?.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true,
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
